import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useMutation } from '@apollo/client';
import Button from '../../../../../../ui/canon/Button';

// RJ Seller Visibility Workflow — buyer's one-time initial contact modal.
// Verbatim confirmation copy from Ashley (Raymond James). See:
// docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md
const RJ_INITIATE_CONTACT = gql`
  mutation RjInitiateContact($seller_user_id: ID!, $body: String!) {
    rj_initiate_contact(seller_user_id: $seller_user_id, body: $body) {
      success
      errors
      conversation_id
    }
  }
`;

// Verbatim from Ashley — do NOT paraphrase without legal sign-off.
const CONFIRMATION_COPY =
  "You will only be able to initiate conversation one time with this seller, and it is at the seller's discretion to message you back. Do you still want to send?";

const RjInitiateContactModal = ({ open, onClose, sellerUserId, sellerName, onSuccess, onAlreadyInitiated }) => {
  const [body, setBody] = useState('');
  const [showingConfirm, setShowingConfirm] = useState(false);
  const [rjInitiate, { loading }] = useMutation(RJ_INITIATE_CONTACT);

  if (!open) return null;

  const handleClose = () => {
    setBody('');
    setShowingConfirm(false);
    onClose && onClose();
  };

  const handleClickSend = (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setShowingConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      const result = await rjInitiate({ variables: { seller_user_id: sellerUserId, body: body.trim() } });
      const payload = result?.data?.rj_initiate_contact;
      if (payload?.success) {
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_first_message_sent', { seller_user_id: sellerUserId });
        }
        onSuccess && onSuccess(payload.conversation_id);
        handleClose();
      } else if ((payload?.errors || []).includes('already_initiated')) {
        if (typeof window !== 'undefined' && window.ahoy) {
          window.ahoy.track('rj_first_message_rejected_prior_conversation', { seller_user_id: sellerUserId });
        }
        onAlreadyInitiated && onAlreadyInitiated();
        handleClose();
      } else {
        alert((payload?.errors || ['Failed to send message']).join(', '));
      }
    } catch (err) {
      console.error('[RJ InitiateContact] failed:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border rounded-lg max-w-lg w-full shadow-xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">
            {showingConfirm ? 'One-time message — confirm?' : `Message ${sellerName || 'seller'}`}
          </h3>
        </div>

        {!showingConfirm && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-600">
              Write your one-time introduction. Once sent, you cannot reach this advisor again through the platform.
            </p>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              maxLength={5000}
              placeholder="Introduce yourself and explain why you'd like to connect…"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        )}

        {showingConfirm && (
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-900 leading-relaxed">{CONFIRMATION_COPY}</p>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer">Message preview</summary>
              <div className="mt-2 border p-3 rounded whitespace-pre-wrap bg-gray-50">{body}</div>
            </details>
          </div>
        )}

        <div className="px-6 py-4 border-t border-succession-border flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {!showingConfirm ? (
            <Button
              variant="primary"
              size="md"
              onClick={handleClickSend}
              disabled={!body.trim() || loading}
            >
              Send
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                size="md"
                onClick={() => setShowingConfirm(false)}
                disabled={loading}
              >
                No, go back
              </Button>
              <Button variant="primary" size="md" onClick={handleConfirm} disabled={loading}>
                {loading ? 'Sending…' : 'Yes, send'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

RjInitiateContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sellerUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sellerName: PropTypes.string,
  onSuccess: PropTypes.func,
  onAlreadyInitiated: PropTypes.func,
};

export default RjInitiateContactModal;
